import { UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, List, Row, Tag } from "antd";
import { buttonStyle, fullWidthStyle, titleStyle } from "../../constants/style";
import { toUploadStatusColor } from "../../constants";
import { File as ApiFile, SubmitStatus } from "../../api/work-order-api";
import ApiContext from "../../api/context";
import { useContext, useEffect, useRef, useState } from "react";
import Dragger from "antd/es/upload/Dragger";
import Paragraph from "antd/es/typography/Paragraph";
import Title from "antd/es/typography/Title";
import { AssetUploadStatuses, Template } from "../../api/models";

type TemplateAssetUploadProps = {
  template?: Template,
  onClose: () => void,
  onReady: (template: Template) => void
};

type TemplateAsset = {
  path: string,
  type: "File" | "File Sequence",
  uploaded: boolean,
  requiredIndex?: string
};

type TemplateAssets = {
  [path: string]: TemplateAsset
};

function createTemplateAssets(assetUploadStatuses?: AssetUploadStatuses): TemplateAssets {
  const templateAssets: TemplateAssets = {};
  for (const file of assetUploadStatuses?.files ?? []) {
    templateAssets[file.path] = {
      path: file.path,
      type: "File",
      uploaded: file.ready
    };
  }
  for (const fileSequence of assetUploadStatuses?.fileSequences ?? []) {
    templateAssets[fileSequence.filePathPattern] = {
      path: fileSequence.filePathPattern,
      type: "File Sequence",
      uploaded: fileSequence.ready,
      requiredIndex: fileSequence.requiredIndex
    };
  }

  return templateAssets;
}

/** Inserts a sequence index into a file sequence's pattern, immediately before the extension. */
function toSequenceFileName(pattern: string, index: string): string {
  const lastSlashIndex = pattern.lastIndexOf("/");
  const lastDotIndex = pattern.lastIndexOf(".");
  if (lastDotIndex > lastSlashIndex) {
    return `${pattern.slice(0, lastDotIndex)}${index}${pattern.slice(lastDotIndex)}`;
  }

  return `${pattern}${index}`;
}

export default function (p: TemplateAssetUploadProps) {
  const [assets, setAssets] = useState<TemplateAssets>({});
  const [assetInitializationStatus, setAssetInitializationStatus] = useState<SubmitStatus>(SubmitStatus.Submitting);
  const [assetSubmitStatus, setAssetSubmitStatus] = useState<SubmitStatus>(SubmitStatus.Idle);
  const [confirmSubmitStatus, setConfirmSubmitStatus] = useState<SubmitStatus>(SubmitStatus.Idle);
  const [error, setError] = useState<string>();
  const [activeAsset, setActiveAsset] = useState<TemplateAsset>();
  const singleFileInputRef = useRef<HTMLInputElement>(null);
  const sequenceFilesInputRef = useRef<HTMLInputElement>(null);
  const { api } = useContext(ApiContext);
  const isInitializing = assetInitializationStatus === SubmitStatus.Submitting;
  const isAssetSubmitting = assetSubmitStatus === SubmitStatus.Submitting;
  const isConfirmSubmitting = confirmSubmitStatus === SubmitStatus.Submitting;
  const canConfirm = !isInitializing &&
    !isAssetSubmitting &&
    !isConfirmSubmitting &&
    Object.values(assets).every(a => a.uploaded);
  const canUploadAsset = !isInitializing && !isAssetSubmitting && !isConfirmSubmitting;

  const uploadFiles = (filesToUpload: ApiFile[]) => {
    if (filesToUpload.length === 0) {
      return;
    }

    setAssetSubmitStatus(SubmitStatus.Submitting);
    api?.postTemplateAssets(p.template?.templateID!, filesToUpload).then(response => {
      if (!response.good() || !response.body) {
        setError("Failed to upload the asset(s).");
        return;
      }

      setAssets(createTemplateAssets(response.body));
      setError(undefined);
    }).catch(_ => {
      setError("Failed to upload the asset(s).");
    }).finally(() => {
      setAssetSubmitStatus(SubmitStatus.Idle);
    });
  };

  const isZipFile = (fileName: string) => fileName.toLowerCase().endsWith(".zip");

  const onAssetTagClicked = (asset: TemplateAsset) => {
    if (!canUploadAsset) {
      return;
    }

    setActiveAsset(asset);
    if (asset.type === "File") {
      singleFileInputRef.current!.value = "";
      singleFileInputRef.current!.click();
    } else {
      sequenceFilesInputRef.current!.value = "";
      sequenceFilesInputRef.current!.click();
    }
  };

  // Load the initial status of the template.
  useEffect(() => {
    api?.getTemplateAssets(p.template?.templateID!).then(response => {
      if (!response.good()) {
        setError("Failed to get the template details.");
        return;
      }

      setAssets(createTemplateAssets(response.body!));
    }).catch(() => {
      setError("Failed to get the template details.");
    }).finally(() => {
      setAssetInitializationStatus(SubmitStatus.Idle);
    });
  }, []);

  return (
    <>
      <Title style={titleStyle} level={4}>Upload Template Assets</Title>
      <Form
        layout="vertical"
        colon={false}>
        <Form.Item label="Template Name">
          <Input
            placeholder="Enter a template name"
            disabled={true}
            value={p.template?.name}
          />
        </Form.Item>
        <Form.Item label="Upload Dependent Files">
          <Dragger
            name="file"
            listType={undefined}
            multiple={true}
            action={undefined}
            showUploadList={false}
            fileList={[]}
            beforeUpload={() => false}
            disabled={false}
            onChange={e => {
              for (const file of e.fileList) {
                if (!file.originFileObj) {
                  setError(`File '${file.name}' could not be read.`);
                  return;
                }
              }

              const filesToUpload: ApiFile[] = [];
              for (const file of e.fileList) {
                const isZip = isZipFile(file.name);
                const isKnownAsset = assets[file.name] !== undefined;
                if (!isZip && !isKnownAsset) {
                  continue;
                }

                filesToUpload.push({
                  fileName: file.name,
                  fileData: file.originFileObj!
                });
              }

              uploadFiles(filesToUpload);
            }}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">Click or drag files to this area to upload.</p>
          </Dragger>
        </Form.Item>
        <Form.Item label="Dependent Files">
          <List
            bordered={false}
            footer={false}
            loading={isInitializing}
            pagination={{ pageSize: 5, hideOnSinglePage: Object.keys(assets).length < 6 }}
            size="small"
            dataSource={Object.values(assets)}
            renderItem={(item: TemplateAsset) =>
              <List.Item key={item.path}>
                <Row
                  justify="space-between"
                  align="middle"
                  style={fullWidthStyle}>
                  <span>{item.path}</span>
                  <Tag
                    color={toUploadStatusColor(item.uploaded)}
                    style={{ cursor: canUploadAsset ? "pointer" : "default" }}
                    onClick={() => onAssetTagClicked(item)}>
                    {item.uploaded ? "Uploaded" : "Not Uploaded"}
                  </Tag>
                </Row>
              </List.Item>
            }
          >
          </List>
          <input
            ref={singleFileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={e => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file || !activeAsset || isZipFile(file.name)) {
                setActiveAsset(undefined);
                return;
              }

              uploadFiles([{ fileName: activeAsset.path, fileData: file }]);
              setActiveAsset(undefined);
            }}
          />
          <input
            ref={sequenceFilesInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={e => {
              const files = Array.from(e.target.files ?? []).filter(file => !isZipFile(file.name));
              e.target.value = "";
              if (files.length === 0 || !activeAsset) {
                setActiveAsset(undefined);
                return;
              }

              const startingIndex = Number(activeAsset.requiredIndex ?? "0");
              const filesToUpload = files.map((file, fileIndex) => ({
                fileName: toSequenceFileName(activeAsset.path, String(startingIndex + fileIndex)),
                fileData: file
              }));

              uploadFiles(filesToUpload);
              setActiveAsset(undefined);
            }}
          />
        </Form.Item>
        <Row style={fullWidthStyle} justify="end" align="middle">
          <Button
            type="default"
            style={buttonStyle}
            disabled={isAssetSubmitting || isConfirmSubmitting}
            onClick={p.onClose}>
            Close
          </Button>
          <Button
            type="primary"
            disabled={!canConfirm}
            style={buttonStyle}
            loading={isConfirmSubmitting}
            onClick={() => {
              setConfirmSubmitStatus(SubmitStatus.Submitting);
              api?.postTemplateAssetConfirm(p.template?.templateID!).then(response => {
                if (!response.good()) {
                  setError("Failed to confirm the template.");
                  return;
                }

                return api?.getTemplate(p.template?.templateID!).then(templateResponse => {
                  if (!templateResponse.good() || !templateResponse.body) {
                    setError("Failed to get the template.");
                    return;
                  }

                  p.onReady(templateResponse.body);
                });
              }).catch(() => {
                setError("Failed to confirm the template.");
              }).finally(() => {
                setConfirmSubmitStatus(SubmitStatus.Idle);
              });
            }}>
            Confirm
          </Button>
        </Row>
        {
          !error ? undefined :
            <>
              <Paragraph></Paragraph>
              <Alert style={fullWidthStyle} type="error" title={error} />
            </>
        }
      </Form>
    </>);
}
